import { ref, computed, watch } from '@vue/composition-api'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import moment from 'moment-timezone'
import store from '@/store'
import { useUtils as useI18nUtils } from "@core/libs/i18n";
import ability from '@/libs/acl/ability'
import { debounce } from 'lodash'
import { getUserData } from '@/auth/utils'
import useTicketCatalogs, { CLASSIFICATION_OPTIONS } from './useTicketCatalogs'
import useUnitSearch from '@/views/helpdesk/useUnitSearch'

// ─────────────────────────────────────────────────────────────
// Catálogos hardcodeados (mientras no haya endpoint propio).
// Prioridades (helpdesk_priorities) y Estatus (helpdesk_statuses).
// ─────────────────────────────────────────────────────────────
export const PRIORITY_MAP = {
  1: 'Muy baja',
  2: 'Bajo',
  3: 'Medio',
  4: 'Alto',
  5: 'Muy alta',
  6: 'Crítico',
}

export const STATUS_MAP = {
  1: 'Nuevo',
  2: 'En curso (asignado)',
  3: 'En curso (planificado)',
  4: 'En espera',
  5: 'Resuelto',
  6: 'Cerrado',
}

export const priorityLabel = id => PRIORITY_MAP[id] || (id ?? '')
export const statusLabel = id => STATUS_MAP[id] || (id ?? '')

export default function useList() {
  const { t } = useI18nUtils();
  const trans = {
    "messages.error.server_error": t("messages.error.server_error"),
  };
  const refListTable = ref(null)

  const toast = useToast()

  const tableColumns = [
    { label: 'Folio', key: 'id_glpi_ticket', sortable: true },
    { label: 'Nombre', key: 'name', sortable: true },
    { label: 'Asunto', key: 'subject', sortable: true },
    {
      label: 'PMI',
      key: 'unit',
      // Soporta ambos shapes: item.units[] (nuevo, múltiple) o item.unit (singular).
      formatter: (value, key, item) => {
        const units = (item && item.units) || []
        if (units.length) {
          return units
            .map(u => u.description || (u.unit && u.unit.description) || `PMI ${u.id || u.id_unit}`)
            .filter(Boolean)
            .join(', ')
        }
        return (value && value.description) || ''
      },
    },
    { label: 'Proveedor', key: 'provider', formatter: value => (value ? [value.name, value.first_name].filter(Boolean).join(' ') || value.email : '') },
    { label: 'Prioridad', key: 'id_priority', sortable: true, formatter: 'formatPriority' },
    { label: 'Estado', key: 'id_status', sortable: true, formatter: 'formatStatus' },
    { label: 'Fecha De Creación', key: 'created_at', sortable: true, formatter: 'formatDateAssigned' },
  ]

  if (ability.can('read', 'HelpdeskTicket') == true || ability.can('delete', 'HelpdeskTicket') == true) {
    tableColumns.splice(0, 0, { label: 'actions.name', key: 'actions' })
  }

  const perPage = ref(10)
  const totalList = ref(0)
  const currentPage = ref(1)
  const perPageOptions = [10, 25, 50, 100]
  const searchQuery = ref('')
  const sortBy = ref('id')
  const isSortDirDesc = ref(true)

  const dataMeta = computed(() => {
    const localItemsCount = refListTable.value ? refListTable.value.localItems.length : 0
    return {
      from: perPage.value * (currentPage.value - 1) + (localItemsCount ? 1 : 0),
      to: perPage.value * (currentPage.value - 1) + localItemsCount,
      of: totalList.value,
    }
  })

  const {
    priorityOptions, providerOptions, loadPriorities, loadProviders,
  } = useTicketCatalogs()
  const { unitOptions, loadingUnits, searchUnits, onUnitSearch, pinUnits } = useUnitSearch()
  const statusOptions = Object.entries(STATUS_MAP).map(([value, label]) => ({ value: Number(value), label }))
  const classificationOptions = CLASSIFICATION_OPTIONS

  const blankFilters = () => ({
    status: [],
    priority: [],
    classification: [],
    unit: [],
    provider: [],
    onlyMine: false,
    startDate: null,
    endDate: null,
  })
  const filters = ref(blankFilters())
  watch(() => filters.value.unit, ids => pinUnits(ids))
  const appliedFilters = ref(blankFilters())

  const filtersCount = computed(() => Object.values(appliedFilters.value)
    .filter(v => (Array.isArray(v) ? v.length : !!v)).length)

  const filterParams = () => {
    const f = appliedFilters.value
    const params = {}
    if (f.status.length) params.id_status = f.status
    if (f.priority.length) params.id_priority = f.priority
    if (f.classification.length) params.classification = f.classification
    if (f.unit.length) params.id_unit = f.unit
    if (f.provider.length) params.id_provider = f.provider
    if (f.onlyMine || !ability.can('assign', 'HelpdeskTicket')) params.involved_user = getUserData().id
    if (f.startDate) params.from = f.startDate
    if (f.endDate) params.to = f.endDate
    return params
  }

  const loadFilterOptions = () => {
    searchUnits()
    loadPriorities().catch(() => {})
    loadProviders().catch(() => {})
  }
  loadFilterOptions()

  const fetchList = (ctx, callback) => {
    store.dispatch('helpdesk/getAllTickets', {
      ...filterParams(),
      q: searchQuery.value,
      max: perPage.value,
      'page[n]': currentPage.value,
      sortBy: sortBy.value,
      sortDesc: isSortDirDesc.value,
    })
      .then(response => {
        callback(response.data)
        totalList.value = response?.meta?.total ?? response.data?.length ?? 0
      })
      .catch(error => {
        console.error('getAllTickets ERROR >>>', error)
        toast({
          component: ToastificationContent,
          props: {
            title: trans['messages.error.server_error'],
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })
  }

  const formatDateAssigned = value => {
    if (!value) return ''
    return moment(value).format('DD/MM/YYYY HH:mm:ss')
  }

  const formatPriority = value => priorityLabel(value)
  const formatStatus = value => statusLabel(value)

  const refetchData = () => {
    refListTable.value.refresh()
  }

  const buildFilters = () => {
    appliedFilters.value = JSON.parse(JSON.stringify(filters.value))
    currentPage.value = 1
    refetchData()
  }

  const clearSearchFilters = () => {
    filters.value = blankFilters()
    buildFilters()
  }

  watch([currentPage, perPage], () => {
    refetchData()
  })

  watch([searchQuery], debounce(() => {
    refetchData()
  }, 500))

  return {
    tableColumns,
    perPage,
    currentPage,
    totalList,
    fetchList,
    dataMeta,
    perPageOptions,
    searchQuery,
    sortBy,
    isSortDirDesc,
    refListTable,
    formatDateAssigned,
    formatPriority,
    formatStatus,
    refetchData,
    // filtros
    filters,
    filtersCount,
    buildFilters,
    clearSearchFilters,
    statusOptions,
    priorityOptions,
    classificationOptions,
    unitOptions,
    loadingUnits,
    onUnitSearch,
    providerOptions,
  }
}